import { useEffect, type RefObject } from 'react'

/**
 * While `open`, makes everything on the page outside `ref` inert, so a modal opened in place,
 * rather than in a portal, hides the page behind it from assistive technology and keeps it from
 * taking focus, as a portaled modal does (WCAG 1.3.2, 2.4.3). What opens later, such as a select's
 * menu in a portal, stays usable. Call it before any hook that gives focus back on close, so the
 * page is usable again by then.
 */
export function useInertOutside(open: boolean, ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const inside = ref.current
    if (!open || inside === null) {
      return undefined
    }
    const made: Element[] = []
    for (let node: HTMLElement | null = inside; node !== null && node !== document.body; node = node.parentElement) {
      for (const sibling of Array.from(node.parentElement?.children ?? [])) {
        if (sibling !== node && !sibling.hasAttribute('inert')) {
          sibling.setAttribute('inert', '')
          made.push(sibling)
        }
      }
    }
    return () => {
      for (const element of made) {
        element.removeAttribute('inert')
      }
    }
  }, [open, ref])
}
